import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle, Ban, Eye, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { moderationAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';

const ACTION_MAP = {
  approve: { label: 'Approve', icon: CheckCircle, color: 'bg-green-500', desc: 'Content is safe' },
  warn: { label: 'Warn User', icon: AlertTriangle, color: 'bg-amber-500', desc: 'Send warning to owner' },
  remove: { label: 'Remove', icon: XCircle, color: 'bg-red-500', desc: 'Remove content' },
  ban_user: { label: 'Ban User', icon: Ban, color: 'bg-red-700', desc: 'Ban the content owner' },
  dismiss: { label: 'Dismiss', icon: Eye, color: 'bg-zinc-600', desc: 'False report' },
};

export default function ModerationPage() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [expandedItem, setExpandedItem] = useState(null);
  const [actionNote, setActionNote] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [queueRes, statsRes] = await Promise.all([
        moderationAPI.queue(activeTab),
        moderationAPI.stats(),
      ]);
      setQueue(queueRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAction = async (contentType, contentId, action) => {
    try {
      await moderationAPI.action(contentType, contentId, { action, note: actionNote });
      toast.success(`Action "${action}" taken successfully`);
      setActionNote('');
      setExpandedItem(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to take action'); }
  };

  if (loading && !stats) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-12" data-testid="moderation-page">
      {/* Header */}
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 flex items-center justify-center rounded-xl">
              <Shield size={20} className="text-violet-600" />
            </div>
            <div>
              <h1 className="font-bold text-2xl text-zinc-900" data-testid="moderation-title">Content Moderation</h1>
              <p className="text-zinc-500 text-sm">Review flagged content and take action</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Pending', value: stats.queue.pending, color: 'text-amber-600 bg-amber-50' },
              { label: 'Reviewed', value: stats.queue.reviewed, color: 'text-blue-600 bg-blue-50' },
              { label: 'Actioned', value: stats.queue.actioned, color: 'text-red-600 bg-red-50' },
              { label: 'Dismissed', value: stats.queue.dismissed, color: 'text-zinc-600 bg-zinc-100' },
              { label: 'Banned Users', value: stats.banned_users, color: 'text-red-700 bg-red-50' },
            ].map((s, i) => (
              <div key={i} className={`p-3 rounded-xl border border-zinc-200 ${s.color.split(' ')[1]}`} data-testid={`mod-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <p className="text-xs text-zinc-500">{s.label}</p>
                <p className={`font-bold text-2xl ${s.color.split(' ')[0]}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Reason Breakdown */}
        {stats?.reason_breakdown && Object.keys(stats.reason_breakdown).length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 mb-6">
            <h3 className="font-semibold text-sm text-zinc-700 mb-3">Report Reasons</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.reason_breakdown).map(([reason, count]) => (
                <span key={reason} className="px-3 py-1 bg-zinc-100 text-zinc-700 text-xs rounded-full">
                  {reason}: <strong>{count}</strong>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Queue */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-zinc-200 p-1 mb-4">
            <TabsTrigger value="pending" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white text-sm">Pending ({stats?.queue.pending || 0})</TabsTrigger>
            <TabsTrigger value="reviewed" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white text-sm">Reviewed</TabsTrigger>
            <TabsTrigger value="actioned" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white text-sm">Actioned</TabsTrigger>
            <TabsTrigger value="dismissed" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white text-sm">Dismissed</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="text-center py-12"><div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : queue.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <CheckCircle className="mx-auto mb-3 text-green-400" size={40} />
                <p className="text-zinc-500">No {activeTab} reports</p>
              </div>
            ) : (
              <div className="space-y-3">
                {queue.map((item, idx) => {
                  const isExpanded = expandedItem === `${item.content_type}:${item.content_id}`;
                  return (
                    <motion.div key={`${item.content_type}:${item.content_id}`} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid={`mod-item-${item.content_id}`}>
                        <button
                          onClick={() => setExpandedItem(isExpanded ? null : `${item.content_type}:${item.content_id}`)}
                          className="w-full p-4 flex items-center justify-between text-left hover:bg-zinc-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${item.report_count >= 3 ? 'bg-red-100' : 'bg-amber-100'}`}>
                              <AlertTriangle size={18} className={item.report_count >= 3 ? 'text-red-500' : 'text-amber-500'} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase">{item.content_type}</span>
                                <span className="font-semibold text-sm text-zinc-900 truncate max-w-xs">{item.content_preview || item.content_id.slice(0, 8)}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-red-500 font-bold">{item.report_count} report{item.report_count > 1 ? 's' : ''}</span>
                                <span className="text-xs text-zinc-400">{item.reasons.join(', ')}</span>
                              </div>
                            </div>
                          </div>
                          <ChevronDown size={16} className={`text-zinc-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>

                        {isExpanded && (
                          <div className="border-t border-zinc-100 p-4 bg-zinc-50">
                            {/* Reports */}
                            <div className="space-y-2 mb-4">
                              {item.reports.map(r => (
                                <div key={r.id} className="bg-white border border-zinc-200 rounded-lg p-3 text-sm">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full">{r.reason}</span>
                                    <span className="text-xs text-zinc-400">{new Date(r.created_at).toLocaleString()}</span>
                                  </div>
                                  {r.details && <p className="text-zinc-600 text-xs mt-1">{r.details}</p>}
                                </div>
                              ))}
                            </div>

                            {/* Action Buttons */}
                            {activeTab === 'pending' && (
                              <div>
                                <Input
                                  placeholder="Admin note (optional)"
                                  value={actionNote}
                                  onChange={e => setActionNote(e.target.value)}
                                  className="mb-3 bg-white"
                                  data-testid="action-note-input"
                                />
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(ACTION_MAP).map(([key, cfg]) => (
                                    <Button
                                      key={key}
                                      onClick={() => handleAction(item.content_type, item.content_id, key)}
                                      className={`${cfg.color} text-white text-xs font-bold`}
                                      data-testid={`action-${key}-btn`}
                                    >
                                      <cfg.icon size={12} className="mr-1" /> {cfg.label}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
